import type { Tag } from "@taskfortress/core"
import { useField, useForm } from "@taskfortress/core"
import { getTagColor } from "@taskfortress/core/src/utils/colors"
import React, { useState, useCallback } from "react"
import { Box } from "../Box"
import { Button } from "../Button"
import { DeleteItemButton } from "../DeleteItemButton"
import { Modal } from "../Modal"
import { Text } from "../Text"
import { Form, TextInput } from "../form"
import { ColorPicker } from "./ColorPicker"

interface TagEditFormProps {
  tag: Tag
  existingTagNames: string[]
  onSave: (updatedTag: Tag) => Promise<void>
  onDelete: (tagId: string) => Promise<void>
  onClose: () => void
}

const TagEditForm: React.FC<TagEditFormProps> = ({
  tag,
  existingTagNames,
  onSave,
  onDelete,
  onClose,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [formError, setFormError] = useState("")
  const [selectedColor, setSelectedColor] = useState(
    tag.color || getTagColor(tag.name),
  )

  // Create name field with validation
  const nameField = useField({
    name: "name",
    initialValue: tag.name,
    validate: (value) => {
      if (!value.trim()) {
        return "Tag name cannot be empty"
      }

      // Check for duplicate tag names, but exclude the current tag's name
      if (
        value.trim() !== tag.name &&
        existingTagNames.includes(value.trim())
      ) {
        return "A tag with this name already exists"
      }

      return ""
    },
  })

  const form = useForm({
    fields: [nameField],
  })

  // Handle color change
  const handleColorChange = useCallback((color: string) => {
    setSelectedColor(color)
  }, [])

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!form.valid) return

    try {
      setIsSaving(true)
      setFormError("")

      // Create updated tag with new name and color
      const updatedTag = {
        ...tag,
        name: nameField.value || "",
        color: selectedColor,
      }

      await onSave(updatedTag)
      onClose()
    } catch (error) {
      setFormError(
        "Failed to save tag: " +
          (error instanceof Error ? error.message : "Unknown error"),
      )
    } finally {
      setIsSaving(false)
    }
  }, [form.valid, nameField.value, selectedColor, tag, onSave, onClose])

  // Handle delete button click
  const handleDelete = useCallback(async () => {
    try {
      await onDelete(tag.id)
      onClose()
    } catch (error) {
      setFormError(
        "Failed to delete tag: " +
          (error instanceof Error ? error.message : "Unknown error"),
      )
    }
  }, [tag.id, onDelete, onClose])

  return (
    <Form onSubmit={handleSubmit} error={formError}>
      <Box className="gap-4">
        <TextInput
          {...nameField}
          label="Tag Name"
          required
          placeholder="Enter tag name"
        />

        <Box>
          <Text className="text-sm font-medium mb-2 text-typography-600">
            Tag Color
          </Text>
          <Box className="flex-row items-center gap-2 mb-2">
            <Box
              className="w-6 h-6 rounded-full"
              style={{ backgroundColor: selectedColor }}
            />
            <Text className="text-sm text-typography-600">{selectedColor}</Text>
          </Box>
          <ColorPicker color={selectedColor} onChange={handleColorChange} />
        </Box>

        <Box className="flex-shrink-0 flex-grow-0 flex-row items-center justify-start gap-2">
          <Button
            variant="primary"
            onPress={handleSubmit}
            disabled={isSaving || !form.valid}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>

          <DeleteItemButton onConfirm={handleDelete} />
        </Box>
      </Box>
    </Form>
  )
}

interface TagEditModalProps {
  tag: Tag
  isVisible: boolean
  onClose: () => void
  onSave: (updatedTag: Tag) => Promise<void>
  onDelete: (tagId: string) => Promise<void>
  existingTagNames: string[]
}

export const TagEditModal: React.FC<TagEditModalProps> = ({
  tag,
  isVisible,
  onClose,
  onSave,
  onDelete,
  existingTagNames,
}) => {
  return (
    <Modal isVisible={isVisible} onClose={onClose} title="Edit Tag" size="sm">
      <TagEditForm
        key={`${tag.id}-${tag.name}`}
        tag={tag}
        existingTagNames={existingTagNames}
        onSave={onSave}
        onDelete={onDelete}
        onClose={onClose}
      />
    </Modal>
  )
}
